import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Box,
  Button,
  Flex,
  Heading,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import { FormTelephoneInput } from '~/components/form-telephone-input';
import { FormTextInput } from '~/components/form-text-input';
import { trackEvent } from '~/features/analytics/track-event';
import { FormalDegreeKey } from '~/features/checkout/constants';
import { DownloadIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { phoneNumberSchema } from '~/features/phone-number/models';

const formSchema = z
  .object({
    firstName: z.string().trim().nonempty(),
    lastName: z.string().trim().nonempty(),
    email: z.string().trim().nonempty().email(),
    phone: phoneNumberSchema.optional(),
    currentEmployer: z.string().optional(),
  })
  .transform((data) => ({
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    phone: data.phone ?? '',
    currentEmployer: data.currentEmployer,
  }));

type FormData = z.infer<typeof formSchema>;

export type AccreditationModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  onSuccess?: () => void;
  formalDegreeKey?: FormalDegreeKey;
};

export function AccreditationModal({ isOpen, closeModal, onSuccess, formalDegreeKey }: AccreditationModalProps) {
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const toast = useToast();

  async function submit(formData: FormData) {
    onSuccess?.();

    trackEvent({
      type: 'submit',
      name: 'Marketing Qualified Lead Form Submitted',
      label: 'master_degree_program',
      additionalAttributes: {
        ...formData,
        phone_number: formData.phone,
        formal_degree_key: formalDegreeKey,
      },
    });

    reset();
    closeModal();
    toast({
      title: 'Thank you for your interest!',
      description: "We'll keep you updated with the latest on this program.",
      variant: 'success',
      duration: null,
    });
  }

  const submitCtaLabel = 'Download Brochure';

  return (
    <Modal isOpen={isOpen} size='2xl' variant='v2' onClose={closeModal}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Heading size='heading-3'>Request a detailed brochure</Heading>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box bg='grey.100' h='1px' my='24px' w='full' />
          <form id='accreditation-form' onSubmit={handleSubmit(submit)}>
            <Flex flexDirection='column' gap='16px' mt='24px'>
              <Flex gap='16px'>
                <FormTextInput
                  errorMessage={errors.firstName?.message}
                  label='First Name*'
                  type='text'
                  {...register('firstName')}
                />
                <FormTextInput
                  errorMessage={errors.lastName?.message}
                  label='Last Name*'
                  type='text'
                  {...register('lastName')}
                />
              </Flex>
              <FormTextInput errorMessage={errors.email?.message} label='Email*' type='email' {...register('email')} />

              <FormTelephoneInput
                control={control}
                errorMessage={errors.phone?.message}
                label='Phone'
                name='phone'
                type='tel'
              />

              <FormTextInput
                errorMessage={errors.currentEmployer?.message}
                label='Current Employer or Institution'
                type='text'
                {...register('currentEmployer')}
              />
            </Flex>
          </form>
        </ModalBody>
        <ModalFooter alignItems='flex-start' flexDirection='column' gap='16px' mt='24px'>
          <Button
            form='accreditation-form'
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
            leftIcon={<DownloadIcon h='18px' w='18px' />}
            type='submit'
          >
            {submitCtaLabel}
          </Button>

          <Text fontSize='xs'>
            By clicking &quot;{submitCtaLabel}&quot; you agree to receive marketing communications from Udacity by
            email, text, and phone call. Call and message rates may apply. By creating an account, you agree to
            Udacity&apos;s{' '}
            <Box as={Link} fontSize='xs' href='/legal/terms-of-use' isExternal>
              Terms of Use
            </Box>{' '}
            and{' '}
            <Box as={Link} fontSize='xs' href='/legal/privacy' isExternal>
              Privacy Policy
            </Box>
            .
          </Text>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
